import React from 'react';
import { Type } from 'lucide-react';
import { LEGEND_STATUSES, TASK_STATUSES } from '../utils/taskStatus';

/**
 * A task's status as a small pill (utils/taskStatus.js: the same words and colours everywhere).
 * `text` (e.g. getTaskBadge's "Overdue · 12 days") replaces the plain status word.
 */
export function TaskStatusPill({ status, text, title, className = '' }) {
  const style = TASK_STATUSES[status];
  if (!style) return null;
  // Timing in words is shown as written (not in capitals), cut off when long - the full text on hover
  const asWritten = status === 'text' && text != null;
  return (
    <span
      data-status={status}
      title={title}
      className={`inline-flex min-w-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide ${asWritten ? 'max-w-[16rem]' : 'uppercase'} ${style.pill} ${className}`}
    >
      {style.icon && <Type className="h-3 w-3 shrink-0" aria-hidden="true" />}
      <span className={asWritten ? 'truncate' : undefined}>{text ?? style.label}</span>
    </span>
  );
}

/** What the status pills mean - shown above the diary list and on the dashboard. */
export function TaskStatusLegend({ className = '' }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300 ${className}`} aria-label="Task status legend" role="list">
      {LEGEND_STATUSES.map((status) => (
        <span key={status} role="listitem" className="inline-flex items-center gap-1">
          <TaskStatusPill status={status} />
          <span>{LEGEND_TEXT[status]}</span>
        </span>
      ))}
    </div>
  );
}

const LEGEND_TEXT = {
  overdue: 'before today',
  soon: 'today or within 2 days',
  week: 'within 7 days',
  later: 'more than 7 days ahead',
  text: 'timing in words, shown as written',
  none: 'no due date (empty, ? or TBD)',
  done: 'completed',
  cancelled: 'cancelled',
};
